import "client-only";
import { useCallback, useEffect, useEffectEvent, useRef } from "react";
import { createObservers, disconnectObservers, setupObservers, startTrackingVisibleElements } from "@/vendors/Snowplow/observation/observation-boundary-utils";
import { isElementInViewport } from "@/vendors/Snowplow/utilities/is-element-in-viewport";
import type { BoundaryObservationData, BoundaryState, BoundaryType, PageLifeCycleEvent } from "../types/observation-boundary-tracker.types";
import { useElementObservations } from "./use-element-observations.hook";
import { usePageLifecycle } from "./use-page-lifecycle.hook";
import { usePageVisibility } from "./use-page-visibility.hook";
type ObservationBoundaryProps = {
  readonly boundaryId: string;
  readonly boundaryType: BoundaryType;
  readonly onBoundaryExit: (data: BoundaryObservationData) => void;
};

/**
 * Tracks element visibility within a boundary container for Snowplow analytics.
 *
 * This hook monitors when elements enter/exit the viewport within a defined
 * boundary and sends tracking data on page lifecycle events (navigation,
 * visibility changes, page unload).
 *
 */
export function useObservationBoundary<T extends HTMLElement>({
  boundaryId,
  boundaryType,
  onBoundaryExit
}: ObservationBoundaryProps) {
  const boundaryRef = useRef<T>(null);
  const elements = useElementObservations();
  const isPageVisible = usePageVisibility();

  // Using ref for mutable state to avoid stale closures in observer callbacks
  // and lifecycle handlers. This pattern ensures callbacks always read fresh
  // values without triggering effect re-runs.
  const stateRef = useRef<BoundaryState>({
    boundaryEnterTime: null,
    isPageVisible: true
  });

  // useEffectEvent creates a stable callback that always sees latest props
  // without being a dependency. This prevents observer recreation when
  // onBoundaryExit callback reference changes.
  const stableOnBoundaryExit = useEffectEvent((data: BoundaryObservationData) => {
    onBoundaryExit(data);
  });

  // Sync React state to ref for access in non-React callbacks (observers)
  useEffect(() => {
    stateRef.current.isPageVisible = isPageVisible;
  }, [isPageVisible]);

  // Getter function allows observers to check visibility without closure issues
  const getIsPageVisible = useCallback(() => stateRef.current.isPageVisible, []);
  const sendExitEvent = useCallback((pageCondition?: PageLifeCycleEvent) => {
    const {
      boundaryEnterTime
    } = stateRef.current;

    // Guard: Only send if we have an active session (user entered boundary)
    if (!boundaryEnterTime) {
      return;
    }

    // Clear enter time first to prevent duplicate events during rapid exits
    stateRef.current.boundaryEnterTime = null;

    // Capture timestamps at exact moment of exit for accurate duration
    const exitTime = new Date();
    const mark = performance.now();

    // Pause all tracking to finalize element time calculations
    elements.pauseAllTracking(exitTime, mark);
    stableOnBoundaryExit({
      boundaryEnterTimestamp: boundaryEnterTime,
      boundaryExitTimestamp: exitTime,
      boundaryId,
      boundaryType,
      elements: elements.getAllElements(),
      pageCondition,
      // sentOnExit indicates if this was triggered by lifecycle event vs scroll
      sentOnExit: pageCondition ? "yes" : "no"
    });
    elements.resetAfterSend();
  }, [boundaryId, boundaryType, elements]);
  const handleBoundaryEnter = useCallback(() => {
    // Mark session start when boundary becomes visible
    stateRef.current.boundaryEnterTime = new Date();
  }, []);
  const handleBoundaryExit = useCallback(() => {
    // Normal scroll exit - no pageCondition since user scrolled away
    sendExitEvent();
  }, [sendExitEvent]);

  // Handle page lifecycle events that should trigger Snowplow emission:
  // - `visibilitychange` (tab switch) - emit because user might not return
  // - `pagehide` / `beforeunload` (tab close) - last chance to send data
  // - `navigation` (Next.js route change) - session ended for this boundary
  usePageLifecycle((event: PageLifeCycleEvent) => {
    if (stateRef.current.boundaryEnterTime) {
      sendExitEvent(event);
    }
  });

  // Pause element tracking when page is hidden,
  // resume visible elements when page becomes active again.
  useEffect(() => {
    const now = new Date();
    const mark_0 = performance.now();
    if (!isPageVisible) {
      // Pause timers for any elements still being tracked.
      // Data was likely already sent by usePageLifecycle, but this ensures
      // clean state if boundary session wasn't active.
      elements.pauseAllTracking(now, mark_0);
      return;
    }

    // User returned to tab — resume tracking for currently visible elements.
    // If the boundary is still in the viewport, restart the boundary session
    // so data tracked after tab-return is captured on the next exit event.
    if (boundaryRef.current) {
      startTrackingVisibleElements(boundaryRef.current, elements, now, mark_0);
      if (isElementInViewport(boundaryRef.current.getBoundingClientRect())) {
        stateRef.current.boundaryEnterTime = now;
      }
    }
  }, [isPageVisible, elements]);

  // Main setup effect - creates and attaches all observers to the boundary
  useEffect(() => {
    const boundary = boundaryRef.current;
    if (!boundary) {
      return;
    }
    const observers = createObservers({
      elements,
      getIsPageVisible,
      onBoundaryEnter: handleBoundaryEnter,
      onBoundaryExit: handleBoundaryExit
    });
    setupObservers(boundary, observers, elements);

    // Cleanup on unmount or dependency change
    return () => {
      disconnectObservers(observers);

      // If boundary was visible when unmounting (e.g., navigation),
      // send final exit event with "navigation" condition
      if (stateRef.current.boundaryEnterTime) {
        sendExitEvent("navigation");
      }
      elements.clear();
    };
  }, [elements, getIsPageVisible, handleBoundaryEnter, handleBoundaryExit, sendExitEvent]);
  return {
    boundaryRef
  };
}